// H-10 to H-13: inviting candidates, one at a time or from a pasted list.
//
// A bulk invite reports per candidate rather than failing as a whole: one bad
// row must not lose the other nineteen, and the reasons matter, because the
// commonest one is H-13 (the email already belongs to someone in the org, so
// they cannot be a candidate: the hire changes the email on the account).

import React, { useState } from 'react';
import { Box, Dialog, Typography } from '@mui/material';
import {
  ActionButton,
  BODY,
  DANGER,
  DONE,
  Field,
  FONT,
  INPUT_SX,
  LINE,
  Segmented,
  SURFACE,
  TEXT,
  TEXT_3,
  TEXTAREA_SX
} from '../capability-setup/designSystem';

export interface InviteRow {
  firstName: string;
  lastName?: string;
  email: string;
}

/**
 * One candidate per line: "First, Last, email" or "First Last <email>", which
 * is what a list pasted out of a spreadsheet or an email thread looks like.
 * Anything unparseable is reported back rather than guessed at.
 */
export const parseInviteList = (text: string): { rows: InviteRow[]; bad: string[] } => {
  const rows: InviteRow[] = [];
  const bad: string[] = [];
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const [email] = line.match(/[^\s,<>;]+@[^\s,<>;]+\.[^\s,<>;]+/) || [];
    if (!email) {
      bad.push(line);
      continue;
    }
    const names = line
      .replace(email, '')
      .replace(/[<>;]/g, '')
      .split(/[,\t]+/)
      .map((part) => part.trim())
      .filter(Boolean);
    const words = names.length > 1 ? names : (names[0] || '').split(/\s+/).filter(Boolean);
    const firstName = words[0] || '';
    if (!firstName) {
      bad.push(line);
      continue;
    }
    rows.push({ firstName, lastName: words.slice(1).join(' '), email });
  }
  return { rows, bad };
};

const InviteDialog: React.FC<{
  open: boolean;
  busy?: boolean;
  error?: string | null;
  refused?: Array<{ email: string; why: string }>;
  onInvite: (rows: InviteRow[]) => void;
  onClose: () => void;
}> = ({ open, busy = false, error, refused = [], onInvite, onClose }) => {
  const [mode, setMode] = useState<'one' | 'many'>('one');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [list, setList] = useState('');

  const parsed = parseInviteList(list);
  const oneReady = firstName.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const ready = mode === 'one' ? Boolean(oneReady) : parsed.rows.length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { bgcolor: SURFACE, border: `1px solid ${LINE}`, borderRadius: '8px' } }}
    >
      <Box sx={{ p: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <Box>
          <Typography sx={{ fontFamily: FONT, fontSize: 18, fontWeight: 600, color: TEXT }}>
            Invite Candidates
          </Typography>
          <Typography sx={{ ...BODY, mt: '4px' }}>
            Each candidate gets one email with a sign-up link. They sign up using email &amp; password, and they
            can&apos;t reach anything else in Dosen outside of this assessment.
          </Typography>
        </Box>

        {/* In a column a Segmented would stretch: this keeps it the size of its two options. */}
        <Box sx={{ alignSelf: 'flex-start' }}>
          <Segmented
            label="How many candidates"
            options={[
              { value: 'one', label: 'One Candidate' },
              { value: 'many', label: 'Multiple Candidates' }
            ]}
            value={mode}
            onChange={setMode}
          />
        </Box>

        {mode === 'one' ? (
          <>
            <Box sx={{ display: 'flex', gap: '10px' }}>
              <Box sx={{ flex: 1 }}>
                <Field label="First Name" required>
                  <Box
                    component="input"
                    value={firstName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
                    sx={INPUT_SX}
                  />
                </Field>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Field label="Last Name">
                  <Box
                    component="input"
                    value={lastName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
                    sx={INPUT_SX}
                  />
                </Field>
              </Box>
            </Box>
            <Field label="Email" required help="It has to be someone who isn't already in your organisation.">
              <Box
                component="input"
                type="email"
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                sx={INPUT_SX}
              />
            </Field>
          </>
        ) : (
          <Field label="One candidate per line" required help="First Name, Last Name, Email">
            <Box
              component="textarea"
              rows={8}
              value={list}
              placeholder="Priya, Raman, priya@example.com"
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setList(e.target.value)}
              sx={TEXTAREA_SX}
            />
            <Typography sx={{ ...BODY, fontSize: 12, color: parsed.rows.length ? DONE : TEXT_3, mt: '6px' }}>
              {parsed.rows.length} {parsed.rows.length === 1 ? 'candidate' : 'candidates'} read
              {parsed.bad.length ? `, ${parsed.bad.length} line(s) we couldn't read` : ''}
            </Typography>
            {parsed.bad.slice(0, 5).map((line) => (
              <Typography key={line} sx={{ ...BODY, fontSize: 12, color: DANGER }}>
                Couldn&apos;t read: {line}
              </Typography>
            ))}
          </Field>
        )}

        {error && (
          <Typography sx={{ ...BODY, color: DANGER }} role="alert">
            {error}
          </Typography>
        )}
        {refused.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {refused.map((row) => (
              <Typography key={row.email} sx={{ ...BODY, fontSize: 12.5, color: DANGER }}>
                {row.email}: {row.why}
              </Typography>
            ))}
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <ActionButton onClick={onClose}>Cancel</ActionButton>
          <ActionButton
            primary
            disabled={!ready || busy}
            reason={!ready ? 'Add at least one candidate with a name and an email' : undefined}
            onClick={() =>
              onInvite(
                mode === 'one'
                  ? [{ firstName: firstName.trim(), lastName: lastName.trim(), email: email.trim() }]
                  : parsed.rows
              )
            }
          >
            {busy ? 'Inviting...' : 'Invite Now'}
          </ActionButton>
        </Box>
      </Box>
    </Dialog>
  );
};

export default InviteDialog;
